import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import userService from '@api/v1/userService'
import { DASHBOARDS_TAB } from '@config/featureFlags'
import { useFirmDashboardsEnabled } from '@hooks/useFirmSettings'
import useReadState from '@hooks/useReadState'

// AI assistant mode. Defaults to 'summary' — preserves current behavior for users
// who haven't chosen (and when settings storage is unavailable).
export const AI_MODES = ['off', 'summary', 'chat']
export const DEFAULT_AI_MODE = 'summary'

/** Per-user-per-tenant settings (server-persisted). Shared react-query cache so every
 *  component reads the same value. */
export const useUserSettings = () =>
  useQuery({
    queryKey: ['user-settings'],
    queryFn: userService.getSettings,
    staleTime: 5 * 60_000,
    retry: false,
  })

/**
 * Merge a partial settings patch into the current settings and persist.
 *
 * CC-53151 — this used to be:
 *
 *     const current = qc.getQueryData(['user-settings']) || {}
 *     return userService.updateSettings({ ...current, ...patch })
 *
 * `getQueryData` returns `undefined` for a query that has not resolved — which
 * includes `fetchStatus: 'paused'` (queryFn never called, nothing thrown, nothing
 * logged) and a plain failure, and note `retry: false` above means a single failure
 * sticks for the whole session. So `current` became `{}` and the PUT carried the
 * patch ALONE. The server was a full replace, so toggling one setting silently
 * deleted every other preference that user had — and returned 200.
 *
 * The chain was worse than either half: wipe the row, and `aiMode: 'off'` is gone;
 * the next successful read finds no `aiMode` and resolves to the default, so an
 * explicit opt-out of an AI feature is reversed PERMANENTLY, with nothing logged.
 *
 * Two changes, and both are needed:
 *
 *  1. `fetchQuery`, not `getQueryData`. It RESOLVES the read instead of sampling
 *     whatever happens to be cached, and it REJECTS if the read fails — so a write
 *     is never built on a value we do not have. A refused save the user can see and
 *     retry is strictly better than a silent partial one.
 *  2. `networkMode: 'always'`. Without it the fetch would PAUSE while react-query
 *     believes the browser is offline, and the mutation would hang forever behind a
 *     spinner — the same lie in a different costume. 'always' makes it attempt the
 *     request and fail fast, which is an answer.
 *
 * cb-api now merges server-side as well (`json_patch`), which is the sturdier fix and
 * protects other clients. This half is kept rather than reduced to sending the bare
 * patch because the two deploy SEPARATELY: a cb2 that posted only the patch to a
 * cb-api that had not yet shipped the merge would wipe the row on every save. Sending
 * the resolved full object is correct against both the old server and the new one.
 */
export const useUpdateUserSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    /*
     * The MUTATION needs 'always' too, not just the read inside it — found by the
     * paused test below, which hung instead of erroring.
     *
     * `useMutation` carries the same `networkMode: 'online'` default as `useQuery`,
     * so while react-query believes the browser is offline `mutate()` parks the
     * mutation in a `paused` state and never calls `mutationFn` at all. The Save
     * button would then sit in `isPending` indefinitely: a write that never happened
     * and never said so, which is this ticket's own defect on the write path.
     */
    networkMode: 'always',
    mutationFn: async (patch) => {
      const current = await qc.fetchQuery({
        queryKey: ['user-settings'],
        queryFn: userService.getSettings,
        staleTime: 5 * 60_000,
        retry: false,
        networkMode: 'always',
      })
      if (!current || typeof current !== 'object' || Array.isArray(current)) {
        throw new Error('Your settings could not be read, so they were not saved. Please try again.')
      }
      return userService.updateSettings({ ...current, ...patch })
    },
    onSuccess: (saved) => qc.setQueryData(['user-settings'], saved),
  })
}

/**
 * The resolved AI mode ('off' | 'summary' | 'chat').
 *
 * CC-53151 — this FAILED OPEN. It was `data?.aiMode` resolved through
 * `AI_MODES.includes(mode) ? mode : DEFAULT_AI_MODE`, and on an unresolved read
 * `data` is undefined, so a user who had explicitly chosen `'off'` got `'summary'`
 * and the AI panel came back. The old comment ("preserves current behavior ... when
 * settings storage is unavailable") shows that was deliberate — but it was decided
 * before we knew a read can silently never happen, and "don't know" must not mean
 * "send this firm's report data to a model".
 *
 * `useShowDashboards` one function below already gets this right, and its docblock
 * states the rule: *"'don't know' and 'not opted in' are the same answer."* An AI
 * opt-out deserves it at least as much as a preview tab does.
 *
 * The DEFAULT is unchanged. A user with a COMPLETED read and no stored `aiMode` still
 * resolves to 'summary' — that is the product default for someone who has never
 * chosen, and this fix must not quietly turn the feature off for them. Only the
 * unresolved read now answers 'off', and only until it resolves.
 */
export const useAiMode = () => {
  const query = useUserSettings()
  const state = useReadState('user-settings', query)
  if (state !== 'ready') return 'off'
  const mode = query.data?.aiMode
  return AI_MODES.includes(mode) ? mode : DEFAULT_AI_MODE
}

/** Whether the user has opted into the Dashboards tab (spec 038). Per-user,
 *  server-persisted; default OFF (opt-in preview while widgets are wired). */
export const useShowDashboards = () => {
  const { data } = useUserSettings()
  return data?.showDashboards === true
}

/**
 * The WHOLE Dashboards gate, in one place — now THREE layers, ANDed:
 *
 *   1. `DASHBOARDS_TAB` (build)  — WHERE the feature is shipped.
 *   2. the FIRM switch (tenant)  — whether this firm uses Dashboards at all
 *                                  (CC-50286, admin-set, runtime-read).
 *   3. the per-user opt-in       — whether this person wants it (default OFF).
 *
 * Every surface that renders a "Dashboards" tab must call this, not the flag
 * alone. Reports.jsx applied layers 1+3 while the Queue and Schedules pages
 * applied only the build flag, so a user who had not opted in still saw a
 * Dashboards tab on those pages — and clicking it landed on /reports, which
 * (correctly) shows the reports catalog and no Dashboards tab at all. The tab
 * advertised a destination that did not exist for that user. One hook, so the
 * three surfaces cannot drift again.
 *
 * PRECEDENCE (spec 083 §3, decided — do not "simplify" this to one value):
 *   - firm OFF beats user ON. A firm that has turned Dashboards off does not want
 *     individuals re-enabling it, so this is an AND, not an override.
 *   - firm ON does NOT force it on: the user's own opt-in still applies, so
 *     firm-on + user-off = off.
 *   - The two defaults differ ON PURPOSE. The per-user layer resolves FALSE while
 *     in flight/unreadable (it is opt-in; "don't know" and "not opted in" are the
 *     same answer). The firm layer resolves TRUE (see useFirmDashboardsEnabled —
 *     'unset' and 'read failed' are the same empty string on the wire, so failing
 *     closed there would disable Dashboards for every firm that never configured
 *     it). ANDing them means the fail-open firm layer can never, on its own,
 *     show the tab to anyone: layer 3 still has to say yes.
 */
export const useDashboardsTab = () => {
  // Read both unconditionally — `DASHBOARDS_TAB && useShowDashboards()` would
  // short-circuit the hook call and break the rules of hooks.
  const firmEnabled = useFirmDashboardsEnabled()
  const showDashboards = useShowDashboards()
  return DASHBOARDS_TAB && firmEnabled && showDashboards
}
